/**
 * [CurveBeamAI] Helpers to apply workflow preset overrides at different lifecycle points.
 *
 * These are pure functions that take the current config + preset and return
 * filtered/overridden copies without mutating the originals.
 */

import { utilities as csUtils } from '@cornerstonejs/core';
import { WorkflowPreset } from './types';
import { WorkflowPresetService } from './WorkflowPresetService';

/**
 * Filter toolbar buttons and sections based on the active preset.
 * Called from onModeEnter before toolbarService.register().
 *
 * @returns { buttons, sections } — filtered copies ready for registration.
 */
export function applyToolbarPreset(
  toolbarButtons: any[],
  toolbarSections: Record<string, any>
): { buttons: any[]; sections: Record<string, any> } {
  const preset = WorkflowPresetService.getActivePreset();
  if (!preset?.toolbar && !preset?.viewportActionMenus) {
    return { buttons: toolbarButtons, sections: toolbarSections };
  }

  const hiddenButtonIds = new Set(preset.toolbar?.hiddenButtonIds ?? []);
  const hiddenSectionKeys = new Set(preset.toolbar?.hiddenSectionKeys ?? []);
  const hiddenMenuKeys = new Set(preset.viewportActionMenus?.hiddenKeys ?? []);
  const sectionOverrides = preset.toolbar?.sectionOverrides ?? {};

  const filteredButtons =
    hiddenButtonIds.size > 0
      ? toolbarButtons.filter(btn => !hiddenButtonIds.has(btn.id))
      : toolbarButtons;

  const filteredSections: Record<string, any> = {};
  for (const [key, value] of Object.entries(toolbarSections)) {
    if (hiddenSectionKeys.has(key)) {
      continue;
    }

    if (key in sectionOverrides) {
      filteredSections[key] = sectionOverrides[key];
      continue;
    }

    if (Array.isArray(value) && (hiddenButtonIds.size > 0 || hiddenMenuKeys.size > 0)) {
      filteredSections[key] = value.filter(
        (id: string) => !hiddenButtonIds.has(id) && !hiddenMenuKeys.has(id)
      );
    } else {
      filteredSections[key] = value;
    }
  }

  return { buttons: filteredButtons, sections: filteredSections };
}

/**
 * Apply panel/layout overrides from the active preset to a cloned layout object.
 * Called from layoutTemplate after structuredCloneWithFunctions.
 *
 * @returns The layout object with overridden panel lists and open/closed defaults.
 */
export function applyLayoutPreset(layout: any): any {
  const preset = WorkflowPresetService.getActivePreset();
  if (!preset?.panels) {
    return layout;
  }

  const { left, right, leftPanelClosed, rightPanelClosed } = preset.panels;

  if (left === false) {
    layout.props.leftPanels = [];
  } else if (Array.isArray(left)) {
    layout.props.leftPanels = left;
  }

  if (right === false) {
    layout.props.rightPanels = [];
  } else if (Array.isArray(right)) {
    layout.props.rightPanels = right;
  }

  if (leftPanelClosed !== undefined) {
    layout.props.leftPanelClosed = leftPanelClosed;
  }

  if (rightPanelClosed !== undefined) {
    layout.props.rightPanelClosed = rightPanelClosed;
  }

  return layout;
}

/**
 * Apply header and misc customizations from the active preset.
 * Called from applyCurveBeamPostInit or onModeEnter.
 *
 * @param customizationService - OHIF CustomizationService instance
 * @param appConfig - The app configuration object (mutated in place for header flags)
 * @param CustomizationScope - The scope enum from @ohif/core
 */
export function applyHeaderAndCustomizations(
  customizationService: any,
  appConfig: any,
  CustomizationScope: any
): void {
  const preset = WorkflowPresetService.getActivePreset();
  if (!preset) {
    return;
  }

  if (preset.header) {
    if (preset.header.showPatientInfo !== undefined) {
      appConfig.showPatientInfo = preset.header.showPatientInfo ? 'visible' : 'disabled';
    }

    if (preset.header.showReturnButton !== undefined) {
      appConfig.showStudyList = preset.header.showReturnButton;
    }
  }

  if (preset.customizations && Object.keys(preset.customizations).length > 0) {
    customizationService.setCustomizations(preset.customizations, CustomizationScope.Global);
  }
}

/**
 * Get the hanging protocol override from the active preset, if any.
 * Returns undefined if no override is set, allowing the caller to fall back
 * to the mode's default.
 */
export function getHangingProtocolOverride(): string | undefined {
  const preset = WorkflowPresetService.getActivePreset();
  return preset?.hangingProtocol;
}

/**
 * Apply the default window/level (VOI) from the active preset to all viewports.
 *
 * Called on VIEWPORT_DATA_CHANGED so the Bone (or other) W/L persists through
 * SEG hydration resets that re-invoke setStack(). The user can still adjust
 * W/L interactively between data-change events.
 *
 * @param cornerstoneViewportService - OHIF CornerstoneViewportService instance
 */
export function applyViewportWindowLevelPreset(cornerstoneViewportService: any): void {
  const preset = WorkflowPresetService.getActivePreset();
  if (!preset?.defaultVoi) {
    return;
  }

  const { windowWidth, windowCenter } = preset.defaultVoi;
  // Convert W/L → VOI range with cornerstone's own helper. It uses the DICOM convention
  // (width = upper - lower + 1), which is the exact inverse of the overlay's VOI → W/L
  // readout. A naive `windowCenter ± windowWidth / 2` instead makes the overlay show
  // 2501/481 for a 2500/480 preset; toLowHighRange round-trips to exactly 2500/480.
  const { lower, upper } = csUtils.windowLevel.toLowHighRange(windowWidth, windowCenter);

  const viewportIds = cornerstoneViewportService.getViewportIds();
  for (const viewportId of viewportIds) {
    const viewport = cornerstoneViewportService.getCornerstoneViewport(viewportId);
    if (viewport?.setProperties) {
      viewport.setProperties({ voiRange: { lower, upper } });
    }
  }
}

/**
 * Apply the viewport image interpolation override from the active preset.
 *
 * Segmentation labelmaps always render with NEAREST interpolation (required to
 * avoid blending discrete label indices). The default CT image uses LINEAR,
 * which smooths voxel edges and creates a visual fringe at segment boundaries.
 * Setting the CT image to NEAREST removes that artificial smoothing so both
 * layers show the true voxel grid — matching MITK's rendering behavior.
 *
 * Called on VIEWPORT_DATA_CHANGED — after StackViewport.setStack() completes —
 * to ensure the setting is not overwritten by the hydration reset to LINEAR.
 *
 * @param cornerstoneViewportService - OHIF CornerstoneViewportService instance
 */
export function applyViewportInterpolationPreset(cornerstoneViewportService: any): void {
  const preset = WorkflowPresetService.getActivePreset();
  if (!preset?.imageInterpolation) {
    return;
  }

  // InterpolationType enum: NEAREST = 0, LINEAR = 1
  const interpolationType = preset.imageInterpolation === 'NEAREST' ? 0 : 1;

  const viewportIds = cornerstoneViewportService.getViewportIds();
  for (const viewportId of viewportIds) {
    const viewport = cornerstoneViewportService.getCornerstoneViewport(viewportId);
    if (viewport?.setProperties) {
      viewport.setProperties({ interpolationType });
    }
  }
}
